import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { ModalCloseButton } from "@circle-react-shared/ModalsV2/FullScreenModal/components/ModalCloseButton";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Button } from "@circleco/compass/components/Button";
import { Modal } from "@circleco/compass/components/Modal";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

import { UpgradeAcceptanceRecovery } from "./UpgradeAcceptanceRecovery";
import { UpgradeOrderDetails } from "./UpgradeOrderDetails";

const i18nRoot = "paywall_checkout.offer_page.upgrade";

interface UpgradeOrderDetailsModalProps {
  isAcceptDisabled: boolean;
  isAccepting: boolean;
  onAccept: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  preview: UpgradePreview;
}

export const UpgradeOrderDetailsModal = ({
  open,
  onOpenChange,
  preview,
  onAccept,
  isAcceptDisabled,
  isAccepting,
}: UpgradeOrderDetailsModalProps) => {
  const isSmScreen = useSmScreenMediaQuery();
  const orderDetails = (
    <>
      <UpgradeAcceptanceRecovery isActive={isAcceptDisabled && !isAccepting} />
      <fieldset disabled={isAcceptDisabled} className="min-w-0">
        <UpgradeOrderDetails preview={preview} />
      </fieldset>
    </>
  );

  if (isSmScreen) {
    return (
      <FullScreenModal
        isOpen={open}
        onClose={() => onOpenChange(false)}
        shouldShowCloseButton
        className="!h-auto !max-h-[80%] rounded-t-xl"
      >
        <FullScreenModal.Header className="border-none !py-4">
          <div className="flex w-full flex-row items-center justify-between">
            <Typography variant="heading-md">
              {t([i18nRoot, "order_summary"])}
            </Typography>
            <ModalCloseButton onClick={() => onOpenChange(false)} />
          </div>
        </FullScreenModal.Header>
        <FullScreenModal.Body className="px-5">
          {orderDetails}
        </FullScreenModal.Body>
        <FullScreenModal.Footer>
          <Button
            variant="primary"
            fullWidth
            disabled={isAcceptDisabled}
            loading={isAccepting}
            onClick={onAccept}
          >
            {preview.labels.cta}
          </Button>
        </FullScreenModal.Footer>
      </FullScreenModal>
    );
  }

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content data-testid="upgrade-order-details-modal">
        <Modal.Header title={t([i18nRoot, "order_summary"])} />
        <Modal.Body>{orderDetails}</Modal.Body>
        <Modal.Footer
          data-testid="upgrade-order-details-footer"
          layout="full-width"
          primaryAction={{
            label: preview.labels.cta,
            onClick: onAccept,
            disabled: isAcceptDisabled,
            loading: isAccepting,
            shouldCloseModal: false,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
